import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search,
  Send,
  Bot,
  UserRound,
  Loader2,
  MessageSquare,
  Phone,
  RotateCcw,
  ExternalLink,
} from "lucide-react";
import {
  useCrmConversas,
  useCrmMensagens,
  useEnviarMensagem,
  useAssumirConversa,
  useDevolverParaIA,
  useCrmCanais,
  useMarcarComoLida,
} from "@/hooks/useCrm";
import { ESTAGIO_COLOR, ESTAGIO_LABEL } from "@/types/crm";
import type { CrmConversa } from "@/types/crm";
import { formatHora, formatTelefone, iniciais } from "./crmUtils";

type Props = {
  conversaId?: string | null;
  onConversaChange?: (id: string | null) => void;
  busca?: string;
  onBuscaChange?: (v: string) => void;
  apenasMinhas?: boolean;
  onApenasMinhasChange?: (v: boolean) => void;
};

export function CrmInbox({
  conversaId = null,
  onConversaChange,
  busca = "",
  onBuscaChange,
  apenasMinhas = false,
  onApenasMinhasChange,
}: Props) {
  const [selecionadaId, setSelecionadaId] = useState<string | null>(conversaId);
  const marcarLida = useMarcarComoLida();

  const { data: conversas = [], isLoading } = useCrmConversas({ busca, apenasMinhas });
  const { data: canais = [] } = useCrmCanais();

  useEffect(() => {
    if (conversaId) setSelecionadaId(conversaId);
  }, [conversaId]);

  const selecionada = useMemo(
    () => conversas.find((c) => c.id === selecionadaId) ?? null,
    [conversas, selecionadaId]
  );

  const abrir = (id: string) => {
    setSelecionadaId(id);
    onConversaChange?.(id);
    const conv = conversas.find((c) => c.id === id);
    if (conv && conv.nao_lidas > 0) marcarLida.mutate(id);
  };

  useEffect(() => {
    if (selecionada && selecionada.nao_lidas > 0) {
      marcarLida.mutate(selecionada.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecionada?.id]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr_280px] gap-4 h-[calc(100vh-280px)] min-h-[520px]">
      <div className="crm-card flex flex-col overflow-hidden">
        <div className="p-3 border-b border-fynix-line space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-fynix-muted" />
            <Input
              placeholder="Buscar por nome ou telefone"
              value={busca}
              onChange={(e) => onBuscaChange?.(e.target.value)}
              className="pl-9 h-10 rounded-full"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-fynix-ink-soft px-1">
            <input
              type="checkbox"
              checked={apenasMinhas}
              onChange={(e) => onApenasMinhasChange?.(e.target.checked)}
              className="rounded border-fynix-line"
            />
            Minhas conversas
          </label>
        </div>
        <ScrollArea className="flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-fynix-muted">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : conversas.length === 0 ? (
            <div className="text-center text-sm text-fynix-muted py-10 px-4">
              Nenhuma conversa ainda. Elas aparecem quando um cliente fala no WhatsApp ou ao disparar uma campanha.
            </div>
          ) : (
            conversas.map((c) => (
              <button
                key={c.id}
                onClick={() => abrir(c.id)}
                className={`w-full text-left px-3 py-3 border-b border-fynix-line transition-colors hover:bg-fynix-surface-muted ${
                  selecionadaId === c.id ? "bg-fynix-brand-soft" : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-fynix-brand-soft text-fynix-brand-dark flex items-center justify-center text-xs font-semibold shrink-0">
                    {iniciais(c.nome_contato, c.telefone_cliente)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-fynix-ink truncate">
                        {c.nome_contato || c.telefone_cliente}
                      </span>
                      <span className="text-[10px] text-fynix-muted shrink-0">{formatHora(c.ultima_msg_em)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-fynix-muted truncate">{c.ultima_msg_preview || "—"}</span>
                      {c.nao_lidas > 0 && (
                        <span className="bg-fynix-brand text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center shrink-0">
                          {c.nao_lidas}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-1">
                      <span className={`${ESTAGIO_COLOR[c.estagio]} text-[10px] px-1.5 py-0 rounded-full border`}>
                        {ESTAGIO_LABEL[c.estagio]}
                      </span>
                      {c.ia_ativa ? (
                        <Bot className="h-3 w-3 text-fynix-brand-dark" />
                      ) : (
                        <UserRound className="h-3 w-3 text-fynix-charcoal" />
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </ScrollArea>
      </div>

      <ChatThread conversa={selecionada} />

      <CardPanel
        conversa={selecionada}
        canalNome={canais.find((c) => c.id === selecionada?.canal_id)?.nome}
      />
    </div>
  );
}

function ChatThread({ conversa }: { conversa: CrmConversa | null }) {
  const { data: mensagens = [], isLoading } = useCrmMensagens(conversa?.id);
  const enviar = useEnviarMensagem();
  const [texto, setTexto] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mensagens.length]);

  if (!conversa) {
    return (
      <div className="crm-card flex flex-col items-center justify-center text-fynix-muted">
        <MessageSquare className="h-10 w-10 mb-2" />
        <p className="text-sm">Selecione uma conversa para visualizar.</p>
      </div>
    );
  }

  const handleEnviar = () => {
    const corpo = texto.trim();
    if (!corpo) return;
    enviar.mutate({ conversa, corpo });
    setTexto("");
  };

  const podeEnviar = !conversa.ia_ativa;

  return (
    <div className="crm-card flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b border-fynix-line flex items-center gap-3">
        <div className="h-8 w-8 rounded-full bg-fynix-brand-soft text-fynix-brand-dark flex items-center justify-center text-xs font-semibold">
          {iniciais(conversa.nome_contato, conversa.telefone_cliente)}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-fynix-ink truncate">
            {conversa.nome_contato || conversa.telefone_cliente}
          </div>
          <div className="text-xs text-fynix-muted flex items-center gap-1">
            <Phone className="h-3 w-3" /> {formatTelefone(conversa.telefone_cliente)}
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 px-4 py-3 bg-fynix-surface-muted">
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-fynix-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="space-y-2">
            {mensagens.map((m) => {
              const isOut = m.direcao === "out";
              return (
                <div key={m.id} className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                      isOut ? "bg-fynix-brand-soft text-fynix-ink" : "bg-white text-fynix-ink"
                    }`}
                  >
                    {(m.autor === "ia" || m.autor === "sistema") && (
                      <div className="text-[10px] font-medium text-fynix-brand-dark mb-0.5 flex items-center gap-1">
                        <Bot className="h-3 w-3" /> {m.autor === "ia" ? "IA" : "Sistema"}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap break-words">{m.corpo}</div>
                    <div className="text-[10px] text-fynix-muted text-right mt-0.5">
                      {formatHora(m.created_at)}
                      {isOut && m.status_envio === "erro" && (
                        <span className="text-red-500 ml-1">• erro</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}
      </ScrollArea>

      <div className="p-3 border-t border-fynix-line">
        {podeEnviar ? (
          <div className="flex items-end gap-2">
            <Textarea
              placeholder="Digite uma mensagem…"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleEnviar();
                }
              }}
              rows={1}
              className="resize-none min-h-[40px] max-h-32 rounded-2xl"
            />
            <Button onClick={handleEnviar} disabled={enviar.isPending || !texto.trim()} className="h-10 rounded-2xl bg-fynix-brand hover:bg-fynix-brand-dark">
              {enviar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        ) : (
          <div className="text-center text-xs text-fynix-muted py-2">
            A IA está atendendo esta conversa. Clique em <strong>Assumir atendimento</strong> no painel ao lado para
            responder manualmente.
          </div>
        )}
      </div>
    </div>
  );
}

function CardPanel({
  conversa,
  canalNome,
}: {
  conversa: CrmConversa | null;
  canalNome?: string;
}) {
  const assumir = useAssumirConversa();
  const devolver = useDevolverParaIA();

  if (!conversa) {
    return <div className="crm-card hidden lg:block" />;
  }

  const abrirCliente = () => {
    if (!conversa.cliente_id) return;
    window.dispatchEvent(new CustomEvent("mudar_secao", { detail: "clientes" }));
  };

  return (
    <div className="crm-card hidden lg:flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b border-fynix-line">
        <h3 className="crm-title text-sm">Painel do card</h3>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          <div>
            <div className="text-xs text-fynix-muted">Contato</div>
            <div className="text-sm font-medium text-fynix-ink">{conversa.nome_contato || "Sem nome"}</div>
            <div className="text-sm text-fynix-ink-soft">{formatTelefone(conversa.telefone_cliente)}</div>
          </div>

          <div>
            <div className="text-xs text-fynix-muted mb-1">Estágio</div>
            <span className={`${ESTAGIO_COLOR[conversa.estagio]} text-xs px-2 py-0.5 rounded-full border`}>
              {ESTAGIO_LABEL[conversa.estagio]}
            </span>
          </div>

          <div>
            <div className="text-xs text-fynix-muted mb-1">Atendimento</div>
            <div className="flex items-center gap-2 text-sm">
              {conversa.ia_ativa ? (
                <>
                  <Bot className="h-4 w-4 text-fynix-brand-dark" />
                  <span>IA ativa</span>
                </>
              ) : (
                <>
                  <UserRound className="h-4 w-4 text-fynix-charcoal" />
                  <span>Humano assumiu</span>
                </>
              )}
            </div>
          </div>

          <div>
            <div className="text-xs text-fynix-muted">Canal</div>
            <div className="text-sm text-fynix-ink-soft">{canalNome ?? "—"}</div>
          </div>

          {conversa.cliente_id ? (
            <button
              type="button"
              onClick={abrirCliente}
              className="text-sm text-fynix-brand-dark font-medium flex items-center gap-1 hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Ver cadastro do cliente
            </button>
          ) : (
            <p className="text-xs text-fynix-muted">Sem cliente vinculado neste thread.</p>
          )}

          <div className="space-y-2 pt-2">
            {conversa.ia_ativa ? (
              <Button
                className="w-full rounded-2xl bg-fynix-brand hover:bg-fynix-brand-dark"
                onClick={() => assumir.mutate(conversa.id)}
                disabled={assumir.isPending}
              >
                {assumir.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <UserRound className="h-4 w-4 mr-2" />
                )}
                Assumir atendimento
              </Button>
            ) : (
              <Button
                variant="outline"
                className="w-full rounded-2xl"
                onClick={() => devolver.mutate(conversa.id)}
                disabled={devolver.isPending}
              >
                {devolver.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                Devolver para a IA
              </Button>
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
